/**
 * React Query hooks for /api/v1/well-testing/* (fieldiq-api#1241, WT-1).
 *
 * EVERY HOOK RETURNS THE META — the tenant's country decides whether a well
 * is labelled by UWI or API number, and `meta.units` is the tenant's entry
 * units.
 *
 * EVERY KEY STARTS ['wellTesting'] and every write invalidates that one
 * prefix: a well write moves the register and the detail; a settings write
 * moves every customer's effective values.
 *
 * EVERY READ SPREADS LOAD_FAILURE_QUERY_OPTIONS, so a refusal is not retried
 * and reads as a refusal (queryLoadFailure), never as an empty list.
 */
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  WtCatalog,
  WtCustomerSettings,
  WtCustomerSettingsUpdate,
  WtMeta,
  WtResult,
  WtSettings,
  WtSettingsUpdate,
  WtWell,
  WtWellWrite,
  WtCrewWrite,
  WtEquipmentSpec,
  WtEquipmentSpecWrite,
  WtFieldJob,
  WtFieldJobDetail,
  WtFieldMeta,
  WtFieldShiftLog,
  WtJob,
  WtJobDetail,
  WtJobEquipment,
  WtJobEquipmentWrite,
  WtJobWrite,
  WtPackage,
  WtPackageDetail,
  WtPackageWrite,
  WtRentInWrite,
  WtShiftLog,
  WtShiftLogSummary,
  WtShiftLogWarning,
  WtShiftLogWrite,
  WtDailyVolume,
  WtDailyVolumes,
  WtDailyVolumeWrite,
  WtFieldReadings,
  WtFlag,
  WtReading,
  WtReadingFields,
  WtReadingWrite,
  WtReport,
  WtReportDetail,
  WtReportWrite,
  WtSeries,
  WtTestPeriod,
  WtImportConfirmBody,
  WtImportParseResult,
  WtImportResult,
} from '../types/well-testing';

/** No zone at all — absent meta prints no time, never a guessed zone. */
const NO_ZONE: string | null = null;

const EMPTY_META: WtMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  country: null,
  timeZone: NO_ZONE,
  today: null,
};

/**
 * Split a body into payload and meta. `data: null` stays null — never the
 * envelope (the `res.data?.data || res.data` bug). A missing meta is all-null,
 * so a screen prints no unit and guesses no country.
 */
export function unwrapWt<T>(body: unknown): WtResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_META };
  const b = body as { data?: unknown; meta?: Partial<WtMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapWtList<T>(body: unknown): WtResult<T[]> {
  const { data, meta } = unwrapWt<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export interface WtWellFilters {
  customerId?: string;
  fluidType?: string;
  provinceState?: string;
  q?: string;
}

export interface WtJobFilters {
  status?: string;
  customerId?: string;
  wellId?: string;
  q?: string;
}

const cleanJobs = (p?: WtJobFilters) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

const clean = (p?: WtWellFilters) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

export const wtKeys = {
  all: ['wellTesting'] as const,
  meta: () => ['wellTesting', 'meta'] as const,
  settings: () => ['wellTesting', 'settings'] as const,
  customerSettings: (id: string) => ['wellTesting', 'customerSettings', id] as const,
  customers: () => ['wellTesting', 'customers'] as const,
  wells: (p?: WtWellFilters) => ['wellTesting', 'wells', clean(p) ?? {}] as const,
  well: (id: string) => ['wellTesting', 'well', id] as const,
  // WT-2
  users: () => ['wellTesting', 'users'] as const,
  equipmentOptions: () => ['wellTesting', 'equipmentOptions'] as const,
  specs: () => ['wellTesting', 'specs'] as const,
  packages: () => ['wellTesting', 'packages'] as const,
  package: (id: string) => ['wellTesting', 'package', id] as const,
  jobs: (p?: WtJobFilters) => ['wellTesting', 'jobs', cleanJobs(p) ?? {}] as const,
  job: (id: string) => ['wellTesting', 'job', id] as const,
  shiftLogs: (jobId: string) => ['wellTesting', 'shiftLogs', jobId] as const,
  shiftLog: (id: string) => ['wellTesting', 'shiftLog', id] as const,
  fieldJobs: () => ['wellTesting', 'field', 'jobs'] as const,
  fieldJob: (id: string) => ['wellTesting', 'field', 'job', id] as const,
  fieldLog: (jobId: string, day: string, shift: string) => ['wellTesting', 'field', 'log', jobId, day, shift] as const,
  // WT-3
  readingFields: () => ['wellTesting', 'readingFields'] as const,
  periods: (jobId: string) => ['wellTesting', 'periods', jobId] as const,
  readings: (jobId: string, all: boolean) => ['wellTesting', 'readings', jobId, all] as const,
  series: (jobId: string, fields: string) => ['wellTesting', 'series', jobId, fields] as const,
  dailyVolumes: (jobId: string) => ['wellTesting', 'dailyVolumes', jobId] as const,
  flags: (jobId: string) => ['wellTesting', 'flags', jobId] as const,
  reports: (jobId: string) => ['wellTesting', 'reports', jobId] as const,
  report: (id: string) => ['wellTesting', 'report', id] as const,
  fieldReadings: (jobId: string) => ['wellTesting', 'field', 'readings', jobId] as const,
};

export const invalidateWt = (qc: QueryClient) => qc.invalidateQueries({ queryKey: wtKeys.all });

/** The catalog (ticket modes, fluid types, quantity units) with the API's labels, plus the tenant meta. */
export function useWtMeta() {
  return useQuery({
    queryKey: wtKeys.meta(),
    queryFn: async () => unwrapWt<WtCatalog>((await api.wellTesting.meta()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtSettings() {
  return useQuery({
    queryKey: wtKeys.settings(),
    queryFn: async () => unwrapWt<WtSettings>((await api.wellTesting.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useUpdateWtSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WtSettingsUpdate) => unwrapWt<WtSettings>((await api.wellTesting.settings.update(body)).data),
    onSuccess: () => invalidateWt(qc),
  });
}

/** One settings read per customer, for the settings page's customer list. */
export function useWtCustomerSettingsList(customerIds: string[]) {
  return useQueries({
    queries: customerIds.map((id) => ({
      queryKey: wtKeys.customerSettings(id),
      queryFn: async () => unwrapWt<WtCustomerSettings>((await api.wellTesting.customerSettings.get(id)).data),
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export function useUpdateWtCustomerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, body }: { customerId: string; body: WtCustomerSettingsUpdate }) =>
      unwrapWt<WtCustomerSettings>((await api.wellTesting.customerSettings.update(customerId, body)).data),
    onSuccess: () => invalidateWt(qc),
  });
}

/** The tenant's customers, for the well form's picker and the settings list. */
export function useWtCustomers(enabled = true) {
  return useQuery({
    queryKey: wtKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtWells(filters?: WtWellFilters) {
  return useQuery({
    queryKey: wtKeys.wells(filters),
    queryFn: async () => unwrapWtList<WtWell>((await api.wellTesting.wells.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtWell(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.well(id ?? ''),
    queryFn: async () => unwrapWt<WtWell>((await api.wellTesting.wells.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreateWtWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WtWellWrite) => unwrapWt<WtWell>((await api.wellTesting.wells.create(body)).data),
    onSuccess: () => invalidateWt(qc),
  });
}

export function useUpdateWtWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: WtWellWrite }) =>
      unwrapWt<WtWell>((await api.wellTesting.wells.update(id, body)).data),
    onSuccess: () => invalidateWt(qc),
  });
}

export function useDeleteWtWell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.wellTesting.wells.remove(id)).data,
    onSuccess: () => invalidateWt(qc),
  });
}

// ═══ WT-2 (fieldiq-api#1245) ═════════════════════════════════════════════════

/** Every WT write invalidates the one prefix and waits for it, so a screen re-reads before it resets a form. */
function useWtWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWt(qc) });
}

/** The tenant's people (not terminated), for the crew and supervisor pickers. */
export function useWtUsers(enabled = true) {
  return useQuery({
    queryKey: wtKeys.users(),
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string; lastName?: string; fullName?: string; role?: string;
        first_name?: string; last_name?: string; employmentStatus?: string; employment_status?: string }>((await api.users.list()).data)
        .filter((u) => (u.employmentStatus ?? u.employment_status) !== 'terminated')
        .map((u) => ({
          id: u.id,
          name: (u.fullName || [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ')).trim() || '—',
          role: u.role ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The shared equipment register (not retired), for the package and job pickers. */
export function useWtEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: wtKeys.equipmentOptions(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; unitNumber?: string | null; unit_number?: string | null;
        serialNumber?: string | null; serial_number?: string | null; status?: string | null }>((await api.equipment.list()).data)
        .filter((e) => e.status !== 'retired')
        .map((e) => ({
          id: e.id,
          name: e.name || '—',
          unitNumber: e.unitNumber ?? e.unit_number ?? null,
          serialNumber: e.serialNumber ?? e.serial_number ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtEquipmentSpecs(enabled = true) {
  return useQuery({
    queryKey: wtKeys.specs(),
    queryFn: async () => unwrapWtList<WtEquipmentSpec>((await api.wellTesting.equipmentSpecs.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWtEquipmentSpec = () =>
  useWtWrite(async ({ equipmentId, body }: { equipmentId: string; body: WtEquipmentSpecWrite }) =>
    unwrapWt<WtEquipmentSpec>((await api.wellTesting.equipmentSpecs.save(equipmentId, body)).data));
export const useDeleteWtEquipmentSpec = () =>
  useWtWrite(async (equipmentId: string) => (await api.wellTesting.equipmentSpecs.remove(equipmentId)).data);

export function useWtPackages() {
  return useQuery({
    queryKey: wtKeys.packages(),
    queryFn: async () => unwrapWtList<WtPackage>((await api.wellTesting.packages.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtPackage(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.package(id ?? ''),
    queryFn: async () => unwrapWt<WtPackageDetail>((await api.wellTesting.packages.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtPackage = () =>
  useWtWrite(async (body: WtPackageWrite) => unwrapWt<WtPackageDetail>((await api.wellTesting.packages.create(body)).data));
export const useUpdateWtPackage = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtPackageWrite }) =>
    unwrapWt<WtPackageDetail>((await api.wellTesting.packages.update(id, body)).data));
export const useDeleteWtPackage = () => useWtWrite(async (id: string) => (await api.wellTesting.packages.remove(id)).data);
export const useAddWtPackageUnit = () =>
  useWtWrite(async ({ packageId, equipmentId }: { packageId: string; equipmentId: string }) =>
    (await api.wellTesting.packages.addUnit(packageId, { equipment_id: equipmentId })).data);
export const useRemoveWtPackageUnit = () => useWtWrite(async (id: string) => (await api.wellTesting.packages.removeUnit(id)).data);

export function useWtJobs(filters?: WtJobFilters, enabled = true) {
  return useQuery({
    queryKey: wtKeys.jobs(filters),
    queryFn: async () => unwrapWtList<WtJob>((await api.wellTesting.jobs.list(cleanJobs(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtJob(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.job(id ?? ''),
    queryFn: async () => unwrapWt<WtJobDetail>((await api.wellTesting.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtJob = () =>
  useWtWrite(async (body: WtJobWrite) => unwrapWt<WtJobDetail>((await api.wellTesting.jobs.create(body)).data));
export const useUpdateWtJob = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtJobWrite }) =>
    unwrapWt<WtJobDetail>((await api.wellTesting.jobs.update(id, body)).data));
export const useDeleteWtJob = () => useWtWrite(async (id: string) => (await api.wellTesting.jobs.remove(id)).data);

export const useAddWtCrew = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtCrewWrite }) => (await api.wellTesting.crew.add(jobId, body)).data);
export const useRemoveWtCrew = () => useWtWrite(async (id: string) => (await api.wellTesting.crew.remove(id)).data);

export const useAddWtJobEquipment = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtJobEquipmentWrite }) =>
    unwrapWtList<WtJobEquipment>((await api.wellTesting.jobEquipment.add(jobId, body)).data));
export const useUpdateWtJobEquipment = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtJobEquipmentWrite }) => (await api.wellTesting.jobEquipment.update(id, body)).data);
export const useRemoveWtJobEquipment = () => useWtWrite(async (id: string) => (await api.wellTesting.jobEquipment.remove(id)).data);

export const useAddWtRentIn = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtRentInWrite }) => (await api.wellTesting.rentIns.add(jobId, body)).data);
export const useUpdateWtRentIn = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtRentInWrite }) => (await api.wellTesting.rentIns.update(id, body)).data);
export const useRemoveWtRentIn = () => useWtWrite(async (id: string) => (await api.wellTesting.rentIns.remove(id)).data);

export function useWtShiftLogs(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.shiftLogs(jobId ?? ''),
    queryFn: async () => unwrapWtList<WtShiftLogSummary>((await api.wellTesting.shiftLogs.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtShiftLog(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.shiftLog(id ?? ''),
    queryFn: async () => unwrapWt<WtShiftLog>((await api.wellTesting.shiftLogs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

const warningsOf = (raw: unknown): WtShiftLogWarning[] => {
  const w = (raw as { warnings?: unknown } | null)?.warnings;
  return Array.isArray(w) ? (w as WtShiftLogWarning[]) : [];
};

export const useCreateWtShiftLog = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtShiftLogWrite }) => {
    const raw = (await api.wellTesting.shiftLogs.create(jobId, body)).data;
    return { ...unwrapWt<WtShiftLog>(raw), warnings: warningsOf(raw) };
  });
export const useUpdateWtShiftLog = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtShiftLogWrite }) => {
    const raw = (await api.wellTesting.shiftLogs.update(id, body)).data;
    return { ...unwrapWt<WtShiftLog>(raw), warnings: warningsOf(raw) };
  });
export const useDeleteWtShiftLog = () => useWtWrite(async (id: string) => (await api.wellTesting.shiftLogs.remove(id)).data);

// ─── Field surface — SNAKE_CASE ──────────────────────────────────────────────

const EMPTY_FIELD_META: WtFieldMeta = { unit_system: null, units: null, currency: null, country: null, time_zone: null, today: null };

export function unwrapWtField<T>(body: unknown): { data: T | null; meta: WtFieldMeta } {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_FIELD_META };
  const b = body as { data?: unknown; meta?: Partial<WtFieldMeta> };
  const data = b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_FIELD_META, ...(b.meta ?? {}) } };
}

export function useWtFieldJobs() {
  return useQuery({
    queryKey: wtKeys.fieldJobs(),
    queryFn: async () => {
      const r = unwrapWtField<WtFieldJob[]>((await api.wellTesting.field.myJobs()).data);
      return { data: Array.isArray(r.data) ? r.data : [], meta: r.meta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtFieldJob(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.fieldJob(id ?? ''),
    queryFn: async () => unwrapWtField<WtFieldJobDetail>((await api.wellTesting.field.job(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtFieldLog(jobId: string | undefined, day: string | undefined, shift: string | undefined) {
  return useQuery({
    queryKey: wtKeys.fieldLog(jobId ?? '', day ?? '', shift ?? ''),
    queryFn: async () => unwrapWtField<WtFieldShiftLog>((await api.wellTesting.field.log(jobId as string, day as string, shift as string)).data),
    enabled: !!jobId && !!day && !!shift,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWtFieldLog = () =>
  useWtWrite(async ({ jobId, day, shift, body }: { jobId: string; day: string; shift: string; body: WtShiftLogWrite }) => {
    const raw = (await api.wellTesting.field.saveLog(jobId, day, shift, body)).data;
    return { ...unwrapWtField<WtFieldShiftLog>(raw), warnings: warningsOf(raw) };
  });

// ═══ WT-3 (fieldiq-api#1248): readings, periods, flags, daily volumes, reports ══

/** The reading fields with the tenant's entry and display units, and the flag kinds. */
export function useWtReadingFields(enabled = true) {
  return useQuery({
    queryKey: wtKeys.readingFields(),
    queryFn: async () => unwrapWt<WtReadingFields>((await api.wellTesting.readingFields()).data),
    staleTime: 5 * 60_000,
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWtPeriods(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.periods(jobId ?? ''),
    queryFn: async () => unwrapWtList<WtTestPeriod>((await api.wellTesting.periods.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtPeriod = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: { kind: string; started_at?: string; notes?: string | null } }) =>
    unwrapWt<WtTestPeriod>((await api.wellTesting.periods.create(jobId, body)).data));
export const useUpdateWtPeriod = () =>
  useWtWrite(async ({ id, body }: { id: string; body: { ended_at?: string | null; notes?: string | null } }) =>
    unwrapWt<WtTestPeriod>((await api.wellTesting.periods.update(id, body)).data));

/** Oldest first, as the API sends them. `all` includes the superseded (corrected) rows. */
export function useWtReadings(jobId: string | undefined, all = false) {
  return useQuery({
    queryKey: wtKeys.readings(jobId ?? '', all),
    queryFn: async () =>
      unwrapWtList<WtReading>((await api.wellTesting.readings.list(jobId as string, all ? { include_superseded: 'true' } : undefined)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The chart series for up to 12 fields (snake_case keys, comma-joined). */
export function useWtSeries(jobId: string | undefined, fields: string[]) {
  const joined = fields.join(',');
  return useQuery({
    queryKey: wtKeys.series(jobId ?? '', joined),
    queryFn: async () => unwrapWt<WtSeries>((await api.wellTesting.readings.series(jobId as string, { fields: joined })).data),
    enabled: !!jobId && fields.length > 0,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtReading = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtReadingWrite }) =>
    unwrapWt<WtReading>((await api.wellTesting.readings.create(jobId, body)).data));
export const useCorrectWtReading = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtReadingWrite & { correction_reason: string } }) =>
    unwrapWt<WtReading>((await api.wellTesting.readings.correct(id, body)).data));

// ─── WT-3b (fieldiq-api#1252): the data-logger import ────────────────────────

/** Parse the shift's logger file. SAVES NOTHING, so it invalidates nothing. */
export const useParseWtLoggerFile = () =>
  useMutation({
    mutationFn: async ({ jobId, file }: { jobId: string; file: File }): Promise<WtResult<WtImportParseResult>> => {
      const form = new FormData();
      form.append('file', file);
      // No Content-Type: the browser sets the multipart boundary (client.ts interceptor).
      const { data, meta } = unwrapWt<WtImportParseResult>((await api.wellTesting.readings.importParse(jobId, form)).data);
      if (!data || typeof data.agentRunId !== 'string' || !Array.isArray(data.rows)) throw new Error('The parse returned no rows to review.');
      return { data, meta };
    },
  });

/** Save the kept rows; the readings, flags and daily volumes all move. */
export const useConfirmWtLoggerImport = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtImportConfirmBody }) => {
    const { data } = unwrapWt<WtImportResult>((await api.wellTesting.readings.importConfirm(jobId, body)).data);
    if (!data || !data.counts || !Array.isArray(data.skipped)) throw new Error('The import returned no result.');
    return data;
  });

/** Abandon a parse (its agent run → dismissed). Saves nothing. */
export const useDismissWtLoggerImport = () =>
  useMutation({
    mutationFn: async ({ jobId, runId }: { jobId: string; runId: string }) =>
      (await api.wellTesting.readings.dismissImport(jobId, runId)).data,
  });

export function useWtDailyVolumes(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.dailyVolumes(jobId ?? ''),
    queryFn: async () => unwrapWt<WtDailyVolumes>((await api.wellTesting.dailyVolumes.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWtDailyVolume = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtDailyVolumeWrite }) =>
    unwrapWt<WtDailyVolume>((await api.wellTesting.dailyVolumes.save(jobId, body)).data));

export function useWtFlags(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.flags(jobId ?? ''),
    queryFn: async () => unwrapWtList<WtFlag>((await api.wellTesting.flags.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useAckWtFlag = () =>
  useWtWrite(async ({ id, note }: { id: string; note: string | null }) =>
    unwrapWt<WtFlag>((await api.wellTesting.flags.ack(id, { note })).data));

export function useWtReports(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.reports(jobId ?? ''),
    queryFn: async () => unwrapWtList<WtReport>((await api.wellTesting.reports.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** One report: `preview` while a draft (built live), `content` once issued (frozen). */
export function useWtReport(id: string | undefined) {
  return useQuery({
    queryKey: wtKeys.report(id ?? ''),
    queryFn: async () => unwrapWt<WtReportDetail>((await api.wellTesting.reports.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWtReport = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtReportWrite }) =>
    unwrapWt<WtReportDetail>((await api.wellTesting.reports.create(jobId, body)).data));
export const useUpdateWtReport = () =>
  useWtWrite(async ({ id, body }: { id: string; body: WtReportWrite }) =>
    unwrapWt<WtReportDetail>((await api.wellTesting.reports.update(id, body)).data));
export const useDeleteWtReport = () => useWtWrite(async (id: string) => (await api.wellTesting.reports.remove(id)).data);
export const useIssueWtReport = () =>
  useWtWrite(async (id: string) => unwrapWt<WtReportDetail>((await api.wellTesting.reports.issue(id)).data));

export function useWtFieldReadings(jobId: string | undefined) {
  return useQuery({
    queryKey: wtKeys.fieldReadings(jobId ?? ''),
    queryFn: async () => unwrapWtField<WtFieldReadings>((await api.wellTesting.field.readings(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWtFieldReading = () =>
  useWtWrite(async ({ jobId, body }: { jobId: string; body: WtReadingWrite }) => (await api.wellTesting.field.saveReading(jobId, body)).data);
